import { type FC, useMemo, useState } from "react";
import { Grid, Group, SimpleGrid, Stack } from "@mantine/core";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useTranslation } from "react-i18next";
import { ReceptionFractionWindow, ReceptionWindowGranularity } from "@/types";
import { SettingsFormContent, SettingsHeader } from "@/components/pages/Settings/SettingsLayout";
import { useDeleteFractionWindow, useUpsertFractionWindow } from "@/services/ReceptionSettings";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import FractionWindowCell from "./FractionWindowCell";
import FractionWindowPanel from "./FractionWindowPanel";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import { useGetAllSalesItemsByLanguageId } from "@/services/Product";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import styles from "./FractionWindowsSection.module.css";

dayjs.extend(isoWeek);

interface GridPeriod {
  periodStart: string;
}

function buildMonthPeriods(year: number): GridPeriod[] {
  return Array.from({ length: 12 }, (_, i) => ({
    periodStart: dayjs().year(year).month(i).startOf("month").format("YYYY-MM-DD"),
  }));
}

/**
 * Every ISO week (Monday start) belonging to the given ISO week-year — 52 or 53 depending on the
 * year. ISO week 1 can start in December of the previous calendar year (e.g. week 1 of 2026 starts
 * 2025-12-29), so this matches by `isoWeekYear()`, not calendar year.
 */
function buildWeekPeriods(year: number): GridPeriod[] {
  const periods: GridPeriod[] = [];
  let cursor = dayjs(`${year}-01-01`).startOf("isoWeek");
  while (cursor.isoWeekYear() === year) {
    periods.push({ periodStart: cursor.format("YYYY-MM-DD") });
    cursor = cursor.add(7, "day");
  }
  return periods;
}

function buildYearPeriods(year: number): GridPeriod[] {
  return [{ periodStart: `${year}-01-01` }];
}

interface Props {
  companyId: number;
  fractionWindows: ReceptionFractionWindow[];
  isLoading: boolean;
}

const FractionWindowsSection: FC<Props> = ({ companyId, fractionWindows, isLoading }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const [granularity, setGranularity] = useState<ReceptionWindowGranularity>("MONTH");
  const [year, setYear] = useState(() => dayjs().year());
  const [activePeriodStart, setActivePeriodStart] = useState<string | null>(null);

  const handleGranularityChange = (value: ReceptionWindowGranularity) => {
    setGranularity(value);
    setActivePeriodStart(null);
  };

  const handleYearChange = (nextYear: number) => {
    setYear(nextYear);
    setActivePeriodStart(null);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["receptionFractionWindows", companyId] });

  const { mutate: upsert } = useUpsertFractionWindow(companyId, {
    onSuccess: () => {
      invalidate();
      setActivePeriodStart(null);
    },
    onError: () =>
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.updateError"), t("notifications:generic.updateError"))
      ),
  });
  const { mutate: deleteWindow } = useDeleteFractionWindow(companyId, {
    onSuccess: () => {
      invalidate();
      closeModal();
      setActivePeriodStart(null);
    },
    onError: () =>
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.updateError"), t("notifications:generic.updateError"))
      ),
  });

  const { data: salesItems = [] } = useGetAllSalesItemsByLanguageId(user?.language.id ?? 1);

  const fractionNameMap = useMemo(
    () => new Map(salesItems.map((item) => [item.salesItemId, item.salesItemTranslated])),
    [salesItems]
  );

  const periods =
    granularity === "MONTH"
      ? buildMonthPeriods(year)
      : granularity === "WEEK"
        ? buildWeekPeriods(year)
        : buildYearPeriods(year);

  const findWindow = (periodStart: string): ReceptionFractionWindow | undefined =>
    fractionWindows.find((w) => w.granularity === granularity && w.periodStart === periodStart);

  const resolveFractionNames = (win: ReceptionFractionWindow | undefined): string[] => {
    if (!win || win.closed || win.acceptAll) return [];
    return win.fractionIds.map((id) => fractionNameMap.get(id) ?? String(id));
  };

  const handleApply = (periodStart: string, partial: Omit<ReceptionFractionWindow, "id">) => {
    const existing = findWindow(periodStart);
    upsert({
      ...partial,
      granularity,
      periodStart,
      ...(existing?.id != null ? { id: existing.id } : {}),
    });
  };

  const handleReset = (windowId: number) => {
    openModal({
      title: t("actions:confirm"),
      content: t("settings:reception.fractionWindows.resetConfirmation"),
      primaryAction: {
        onClick: () => deleteWindow(windowId),
        text: t("settings:reception.fractionWindows.resetWindow"),
        intent: "negate",
      },
      secondaryAction: {
        onClick: () => closeModal(),
        text: t("actions:cancel"),
        intent: "primary",
      },
    });
  };

  const activePeriod = activePeriodStart ? periods.find((p) => p.periodStart === activePeriodStart) : null;

  const dayjsLocale = i18n.language.startsWith("no") ? "nb" : "en";

  const activePeriodLabel = activePeriodStart
    ? granularity === "MONTH"
      ? dayjs(activePeriodStart).locale(dayjsLocale).format("MMMM YYYY")
      : granularity === "WEEK"
        ? `${t("settings:reception.fractionWindows.granularity.week")} ${dayjs(activePeriodStart).isoWeek()}`
        : String(dayjs(activePeriodStart).year())
    : "";

  return (
    <>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong display="technical" tt="uppercase">
              {t("settings:reception.fractionWindows.title")}
            </Text>
            <Group gap="sm">
              <Group gap={4} className={styles.yearNav}>
                <button
                  type="button"
                  className={styles.yearNavBtn}
                  aria-label={t("settings:reception.fractionWindows.previousYear")}
                  onClick={() => handleYearChange(year - 1)}
                >
                  <SVGAsset name="chevron-right" width={10} height={10} style={{ transform: "rotate(180deg)" }} />
                </button>
                <Text size="sm" strong display="technical" className={styles.yearNavLabel}>
                  {year}
                </Text>
                <button
                  type="button"
                  className={styles.yearNavBtn}
                  aria-label={t("settings:reception.fractionWindows.nextYear")}
                  onClick={() => handleYearChange(year + 1)}
                >
                  <SVGAsset name="chevron-right" width={10} height={10} />
                </button>
              </Group>
              <SegmentedControls
                data={[
                  { label: t("settings:reception.fractionWindows.granularity.week"), value: "WEEK" },
                  { label: t("settings:reception.fractionWindows.granularity.month"), value: "MONTH" },
                  { label: t("settings:reception.fractionWindows.granularity.year"), value: "YEAR" },
                ]}
                value={granularity}
                onChange={(v) => handleGranularityChange(v as ReceptionWindowGranularity)}
                disabled={isLoading}
              />
            </Group>
          </SettingsHeader>

          <Grid.Col span={{ base: 12, md: activePeriod ? 8 : 12 }}>
            {granularity === "MONTH" ? (
              <SimpleGrid cols={{ base: 2, sm: 3, md: activePeriod ? 3 : 4 }} spacing="sm">
                {periods.map(({ periodStart }) => (
                  <FractionWindowCell
                    key={periodStart}
                    periodStart={periodStart}
                    granularity={granularity}
                    fractionNames={resolveFractionNames(findWindow(periodStart))}
                    window={findWindow(periodStart)}
                    isActive={periodStart === activePeriodStart}
                    onClick={() => setActivePeriodStart(periodStart)}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <Stack gap="sm">
                {periods.map(({ periodStart }) => (
                  <FractionWindowCell
                    key={periodStart}
                    periodStart={periodStart}
                    granularity={granularity}
                    fractionNames={resolveFractionNames(findWindow(periodStart))}
                    window={findWindow(periodStart)}
                    isActive={periodStart === activePeriodStart}
                    onClick={() => setActivePeriodStart(periodStart)}
                  />
                ))}
              </Stack>
            )}
          </Grid.Col>

          {activePeriod && (
            <Grid.Col span={{ base: 12, md: 4 }}>
              <FractionWindowPanel
                key={activePeriod.periodStart}
                periodLabel={activePeriodLabel}
                window={findWindow(activePeriod.periodStart)}
                onClose={() => setActivePeriodStart(null)}
                onApply={(partial: Omit<ReceptionFractionWindow, "id">) =>
                  handleApply(activePeriod.periodStart, partial)
                }
                onReset={
                  findWindow(activePeriod.periodStart)?.id != null
                    ? () => handleReset(findWindow(activePeriod.periodStart)!.id!)
                    : undefined
                }
              />
            </Grid.Col>
          )}

          <Grid.Col span={12}>
            <Group gap="lg" className={styles.legend}>
              <Group gap={6}>
                <span className={`${styles.legendSwatch} ${styles.legendSwatchOpen}`} />
                <Text size="xs" color="secondary">
                  {t("settings:reception.fractionWindows.legend.open")}
                </Text>
              </Group>
              <Group gap={6}>
                <span className={`${styles.legendSwatch} ${styles.legendSwatchSelection}`} />
                <Text size="xs" color="secondary">
                  {t("settings:reception.fractionWindows.legend.selection")}
                </Text>
              </Group>
              <Group gap={6}>
                <span className={`${styles.legendSwatch} ${styles.legendSwatchClosed}`} />
                <Text size="xs" color="secondary">
                  {t("settings:reception.fractionWindows.legend.closed")}
                </Text>
              </Group>
            </Group>
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    </>
  );
};

export default FractionWindowsSection;
